import { dataService } from '../../data';
import { AppConstants } from '@unpispas/upp-defs';
import { logsService } from '@unpispas/upp-base';
import { httpService } from '@unpispas/upp-base';

import { Subject } from 'rxjs';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { User } from './user';
import { ObjectFactory } from '../factory';
import { Address } from './address';
import { DeviceOption } from './deviceopt';
import { PlaceLink } from './placelink';
import { PlaceOption } from './placeopt';
import { Ticketbai } from './ticketbai';

import { PlaceView, PlaceViewProxy } from '../views/place';

import type { Coordinates } from '@unpispas/upp-base';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { UserType } from '../types';
import { AddressType } from '../types';
import { AuditType } from '../types';
import { QrCodeType } from '../types';
import { RasppiType } from '../types';
import { PlaceOptionType } from '../types';
import { PlaceLinkType } from '../types';
import { I18nValueType } from '../types';
import { ProductType } from '../types';
import { OfferType } from '../types';
import { ExtraType } from '../types';
import { DiscountType } from '../types';
import { EmployeeType } from '../types';
import { FloorAreaType } from '../types';
import { TicketType } from '../types';
import { TillType } from '../types';
import { ClientType } from '../types';
import { PayAccountType } from '../types';
import { PaymentType } from '../types';
import { StripeType } from '../types';
import { PriceZoneType } from '../types';
import { PriceTimeType } from '../types';

const _schema = {
    "name": "PLACE",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "user", type: "objid" },
        { name: "name", type: "string" },
        { name: "description", type: "objid" },
        { name: "photo", type: "file" },
        { name: "logo", type: "file" },
        { name: "expires", type: "date" },
        { name: "address", type: "objid" },
        { name: "business", type: "string" },
        { name: "company", type: "boolean" },
        { name: "activity", type: "string" },
        { name: "taxid", type: "string" },
        { name: "fiscal", type: "objid" },
        { name: "phone", type: "string" }
    ],
    relate: [
        { direction: ">", target: "USER", by: "user", name: "user",  reason: "user", child: false, class: UserType },        
        { direction: ">", target: "ADDRESS", by: "address", name: "address", reason: "address", child: true, class: AddressType },
        { direction: ">", target: "ADDRESS", by: "fiscal", name: "fiscal",  reason: "fiscal", child: true, class: AddressType },
        { direction: ">", target: "I18NVALUE", by: "description", name: "description", reason: "description", child: true, class: I18nValueType },
        { direction: "<", target: "QRCODE", by: "place", name: 'qrcode', reason: "qrcodes", child: true, class: QrCodeType },
        { direction: "<", target: "FLOORAREA", by: "place", name: "drawarea", reason: "drawareas", child: true, class: FloorAreaType },
        { direction: "<", target: "STRIPE", by: "place", name: "stripe", reason: "stripes", child: true, class: StripeType },
        { direction: "<", target: "PAYACCOUNT", by: "place", name: "account", reason: "accounts", child: true, class: PayAccountType },
        { direction: "<", target: "RASPPI", by: "place", name: "service", reason: "services", child: true, class: RasppiType },
        { direction: "<", target: "PLACEOPT", by: "place", name: "option", reason: "options", child: true, class: PlaceOptionType },
        { direction: "<", target: "PLACELINK", by: "place", name: "link", reason: "links", child: true, class: PlaceLinkType },
        { direction: "<", target: "PRODUCT", by: "place", name: "product", reason: "products", child: true, class: ProductType },
        { direction: "<", target: "OFFER", by: "place", name: "offer", reason: "offers", child: true, class: OfferType },
        { direction: "<", target: "EXTRA", by: "place", name: "extra", reason: "extras", child: true, class: ExtraType },
        { direction: "<", target: "DISCOUNT", by: "place", name: "discount", reason: "discounts", child: true, class: DiscountType },
        { direction: "<", target: "STAFF", by: "place", name: "employee", reason: "employees", child: true, class: EmployeeType },
        { direction: "<", target: "TICKET", by: "place", name: "ticket", reason: "tickets", child: true, class: TicketType },
        { direction: "<", target: "INVOICEBUSINESS", by: "place", name: "client", reason: "clients", child: true, class: ClientType },
        { direction: "<", target: "AUDIT", by: "place", name: "audit", reason: "audits", child: true, class: AuditType },
        { direction: "<", target: "TILL", by: "place", name: "till", reason: "tills", child: true, class: TillType },
        { direction: "<", target: "PRICEZONE", by: "place", name: "pricezone", reason: "pricezones", child: true, class: PriceZoneType },
        { direction: "<", target: "PRICETIME", by: "place", name: "pricetime", reason: "pricetimes", child: true, class: PriceTimeType },
        { direction: "<", target: "PAYMENT", by: "place", name: "transaction", reason: "transactions", child: true, class: PaymentType },
    ]
} as const;

export function PlaceClass() {
    return Place;
}

const _PlaceClass = CreateObject(_schema);
if (!_PlaceClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Place extends _PlaceClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Place {
        return this._Copy(store) as Place;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'user' && related instanceof User){
            if (related === this.user){
                related.AddPlace(this);
            }
        }
    }

    override get viewProxy(): PlaceView {
        return PlaceViewProxy(this, this.data);
    }

    /****************************/
    /* SERVICE INJECTORS        */
    /****************************/

    private _http: httpService | null = null;
    private get http(): httpService {
        if (!this._http){
            this._http = this.data.injector.get(httpService);
        }

        return this._http;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get avatar(): string | null {
        const _bg = 'ffffff';
        const _fg = '000000';

        if (!this.name){
            return null;
        }

        return AppConstants.baseURL + 'photo/avatar?size=128&background=' + _bg + '&color=' + _fg + '&name=' + encodeURIComponent(this.name);
    }

    get photo(): string | null {
        return this['__base__photo'] || this.avatar;
    }

    set photo(value: string | null){
        this['__base__photo'] = value;
    }

    get status(): string | null {
        return this['__base__status']
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;
        if (this.user){
            if ((this.status == 'DE') && !this.Commited){
                this.user.DelPlace(this);
            }
            else {
                this.user.DoRefresh(this.table);
            }    
        }
    }

    /****************************/
    /* PLACE CONFIGURATION      */
    /****************************/

    private getOptionByKey(opkey: string): PlaceOption | null {
        let _selected: PlaceOption | null = null;

        for (const _option of this.options as PlaceOption[]){
            if (_option.opkey !== opkey){
                continue;
            }

            if (!_selected){
                _selected = _option;
                continue;
            }

            const _selectedUpdated = _selected.updated?.getTime() || 0;
            const _optionUpdated = _option.updated?.getTime() || 0;
            if (_optionUpdated >= _selectedUpdated){
                _selected = _option;
            }
        }

        return _selected;
    }

    GetConfigValue(key: string): string | null {
        const _option = this.getOptionByKey(key);
        const _value = _option ? _option.value : null;
        return (_value != null) ? String(_value) : null;
    }

    GetConfigOption(key: string, defval = false): boolean {
        const _option = this.getOptionByKey(key);
        if (_option){
            const _value = _option.value;
            return !!_value && (_value != '0');
        }

        return defval;
    }

    SetConfigOption(opkey: string, opval: string | boolean | null): void {
        const _value = (typeof opval === 'boolean') ? (opval ? '1' : '0') : (opval || null);

        const _option = this.getOptionByKey(opkey);
        if (_option){
            _option.value = _value;
        }
        else if (_value != null) {
            const _newOption = ObjectFactory.object('PLACEOPT', null, this.data) as PlaceOption;
            _newOption.place = this;
            _newOption.opkey = opkey;
            _newOption.value = _value;
            this.AddOption(_newOption);
        }
    }

    GetPlaceLink(linkKey: string): PlaceLink | null {
        for (const _link of this.links){
            if (_link.link === linkKey){
                return _link;
            }
        }
        return null;
    }

    /**
     * Attached menu PDF (`link === 'pdfmenu'`) when a URL or pending upload exists.
     */
    get PdfMenu(): PlaceLink | null {
        const _link = this.GetPlaceLink('pdfmenu');
        if (_link && (_link.url || _link.base64)){
            return _link;
        }
        return null;
    }

    /**
     * Creates or updates a PLACELINK row. Pass null/blank URL and null `lkb64` to clear both.
     * Does not create a new row when both values are empty.
     */
    SetPlaceLink(linkKey: string, lkurl: string | null, lkb64: string | null = null): void {
        const _trimmed = lkurl != null ? String(lkurl).trim() : '';
        const _url = _trimmed !== '' ? _trimmed : null;
        const _b64 = lkb64 != null && String(lkb64).trim() !== '' ? String(lkb64) : null;

        let _link = this.GetPlaceLink(linkKey);
        if (!_link){
            if (_url == null && _b64 == null){
                return;
            }
            _link = ObjectFactory.object('PLACELINK', null, this.data) as PlaceLink;
            _link.place = this;
            _link.link = linkKey;
            this.AddLink(_link);
        }

        _link.url = _url;
        _link.base64 = _b64;
    }

    private getOptionByDevice(option: PlaceOption, device: string): DeviceOption | null {
        let _selected: DeviceOption | null = null;
        for (const _deviceOption of ((option.devices as DeviceOption[]) || [])){
            const _deviceKey = _deviceOption.device ?? '';
            if (_deviceKey !== device) {
                try {
                    if (decodeURIComponent(_deviceKey) !== decodeURIComponent(device)) {
                        continue;
                    }
                }
                catch {
                    continue;
                }
            }

            if (!_selected){
                _selected = _deviceOption;
                continue;
            }

            const _selectedUpdated = _selected.updated?.getTime() || 0;
            const _optionUpdated = _deviceOption.updated?.getTime() || 0;
            if (_optionUpdated >= _selectedUpdated){
                _selected = _deviceOption;
            }
        }

        return _selected;
    }

    private _LoggerCampaign(device: string): string | null {
        const _option = this.getOptionByKey('optionLoggerCampaign');
        if (!_option){
            return null;
        }

        const _deviceOption = this.getOptionByDevice(_option, device);
        if (!_deviceOption || (_deviceOption['status'] == 'DE') || (_deviceOption.value == null)){
            return null;
        }

        return String(_deviceOption.value);
    }

    /**
     * Starts/stops the logger campaign configured for this place's device.
     * Single orchestration point for logger runtime state: sync-driven updates via
     * `DeviceOption._OnUpdate`, place entry via `dataService.SetPlace`.
     */
    Campaign(device: string): void {
        if (this.data.place != this || !device || device != this.data.device){
            return;
        }

        setTimeout(() => {
            const _campaign = this._LoggerCampaign(device);
            const _logs = this.data.injector.get(logsService);
            if (_campaign == _logs.Campaign){
                return;
            }

            if (_campaign){
                _logs.Start(_campaign);
            }
            else {
                _logs.Stop();
            }
        }, 0);
    }

    /**********************************/
    /* CUSTOM CONFIGURATION GETTERS    */
    /**********************************/

    get lang(): string {
        return this.GetConfigValue('optionLanguage') || 'ES';
    }

    get FiscalWarning(): boolean {
        return !this.GetConfigOption('optionFiscalReady', false);
    }

    get PosWarning(): boolean {
        return !this.HasTill;
    }

    get CanPayTickets(): boolean {
        if (this.IsExpired){
            return false;
        }

        if (!this.GetConfigOption('optionFiscalReady', false)){
            return false;
        }

        return this.HasTill;
    }

    /** Whether this place has at least one operational till (valid rasppi + connected terminal). */
    get HasTill(): boolean {
        for (const _till of this.tills || []){
            if (_till.IsValid){
                return true;
            }
        }

        return false;
    }

    /**
     * Whether the session device requires a local print-service backup
     * (`PosDevice.connect.IsLocal`). False when there is no session device
     * or no active local connect.
     */
    get RequiresLocalBackup(): boolean {
        return this.data.session?.device?.connect?.IsLocal ?? false;
    }

    /**
     * Whether the WS pending-changes store is trustworthy right now.
     * True when local backup is not required, or when it is required and confirmed.
     */
    get WSStorageReady(): boolean {
        return !this.RequiresLocalBackup || this._backupReady;
    }

    /**
     * Whether a new ticket may be created/committed given license and local backup.
     * Does not consult online connectivity — that gate belongs to payment.
     */
    get AllowTicketCommit(): boolean {
        return !this.IsExpired && this.WSStorageReady;
    }

    /**
     * Whether a ticket may be paid/closed (definitive fiscal document).
     * Requires CanPayTickets and AllowTicketCommit, plus online connectivity when
     * local backup is not available, and TicketBAI signing libs when BAI is on
     * and the device is offline. Connectivity is read from `this.http.IsOnline`.
     */
    get AllowTicketPayment(): boolean {
        if (!this.CanPayTickets || !this.AllowTicketCommit){
            return false;
        }

        const _online = this.http.IsOnline;

        if (!this.RequiresLocalBackup && !_online){
            return false;
        }

        if (this.SendBAIEnabled && !_online && !Ticketbai.LibrariesReady){
            return false;
        }

        return true;
    }

    /**
     * Whether the local backup store itself is ready (recovered and usable).
     * Meaningful only when `RequiresLocalBackup` is true. Written by sync after
     * connect+recover; cleared when the local link drops. Features read
     * `WSStorageReady`, not this flag.
     */
    private _backupReady = false;

    get BackupReady(): boolean {
        return this._backupReady;
    }

    set BackupReady(value: boolean) {
        const _ready = !!value;
        if (this._backupReady === _ready){
            return;
        }

        this._backupReady = _ready;
        this.DoRefresh(this.table);
    }

    get MultiplePOS(): boolean {
        return this.GetConfigOption('optionMultiplePOS', false);
    }

    /** Whether the session device operates as TPV on this place. */
    get IsPos(): boolean {
        const _device = this.data.session?.device;
        if (!_device?.IsCurrent || this.data.place !== this){
            return false;
        }

        return _device.IsPos;
    }

    get ProductCodes(): boolean {
        return this.GetConfigOption('optionProductCodes', false);
    }

    get taxrate(): number {
        const raw = this.GetConfigValue('optionTaxRate');
        if (raw != null && raw !== ''){
            const n = Number(raw);
            if (!Number.isNaN(n)){
                return n;
            }
        }
        return AppConstants.defaultTaxRate;
    }

    get TicketPrepayment(): boolean {
        return this.GetConfigOption('optionPrePayment', false);
    }

    get TicketPreparation(): boolean {
        return this.GetConfigOption('optionTrackTicket', false);
    }

    /** Whether table service (waiter) is enabled for guest flows. */
    get TableService(): boolean {
        return this.GetConfigOption('optionTableService', false);
    }

    /** Whether QR ordering is enabled for this place. */
    get AcceptQR(): boolean {
        return this.GetConfigOption('optionQrOrders', false);
    }

    /**
     * Whether guest QR flows must verify diner proximity (default on when unset).
     */
    get QrGeolocation(): boolean {
        return this.GetConfigOption('optionQrGeolocation', true);
    }

    /** Alias of TicketPrepayment — guest UI uses this name (parity with v3). */
    get IsPrepay(): boolean {
        return this.GetConfigOption('optionPrePayment', false);
    }

    /** Bar kitchen open for QR orders (requires AcceptQR). */
    get IsBarOpen(): boolean {
        return this.AcceptQR && this.GetConfigOption('barIsOpen', false);
    }

    set IsBarOpen(value: boolean) {
        this.SetConfigOption('barIsOpen', value);
    }

    /** Kit kitchen open for QR orders (requires AcceptQR). */
    get IsKitOpen(): boolean {
        return this.AcceptQR && this.GetConfigOption('kitIsOpen', false);
    }

    set IsKitOpen(value: boolean) {
        this.SetConfigOption('kitIsOpen', value);
    }

    /** Minimum amount for card payment (0 when unset). */
    get CardMinimum(): number {
        const raw = this.GetConfigValue('amountCreditCard');
        const value = raw != null ? Number(raw) : 0;
        return Number.isNaN(value) ? 0 : value;
    }

    /** Minimum amount for online payment (0 when unset). */
    get OnlineMinimum(): number {
        const raw = this.GetConfigValue('amountOnlinePay');
        const value = raw != null ? Number(raw) : 0;
        return Number.isNaN(value) ? 0 : value;
    }

    /**
     * Whether a PLACEOPT row exists for opkey (any value).
     * Used to show config banners only when the place actually configured the option.
     */
    HasConfiguredOption(opkey: string): boolean {
        return this.getOptionByKey(opkey) != null;
    }

    get CanCash(): boolean {
        return true;
    }

    get CanCard(): boolean {
        return this.GetConfigOption('optionCreditCard', false);
    }

    get CanLine(): boolean {
        return this.GetConfigOption('optionOnlinePay', false);
    }

    get CanAccount(): boolean {
        if (this.IsExpired){
            return false;
        }

        return this.GetConfigOption('optionPayAccount', false);
    }

    get OpenOnCash(): boolean {
        return this.GetConfigOption('optionOpenCashpay', false);
    }

    get OpenOnCard(): boolean {
        return this.GetConfigOption('optionOpenCardpay', false);
    }

    get CashModal(): boolean {
        return this.GetConfigOption('optionCashModal', false);
    }

    get CardModal(): boolean {
        return this.GetConfigOption('optionCardModal', false);
    }

    private get VerifactuAvailable(): boolean {
        const fiscal = this.fiscal as Address | null;
        if (!fiscal?.formatted){
            return false;
        }

        return !this.TicketbaiAvailable;
    }

    private get TicketbaiAvailable(): boolean {
        const fiscal = this.fiscal as Address | null;
        if (!fiscal?.formatted){
            return false;
        }

        const _address = fiscal.formatted;
        const _provinces = [
            /Bizkaia/i, /Vizcaya/i,
            /Guipúzcoa/i, /Guipuzcoa/i, /Gipuzkoa/i,
            /Álava/i, /Alava/i, /Araba/i,
        ];

        for (const _pattern of _provinces){
            if (_address.search(_pattern) !== -1){
                return true;
            }
        }

        return false;
    }

    private _safedisable_vft = false;
    get SendVFTEnabled(): boolean {
        return !this._safedisable_vft && this.VerifactuAvailable && this.GetConfigOption('optionVftEnabled', false);
    }

    private _safedisable_bai = false;
    get SendBAIEnabled(): boolean {
        return !this._safedisable_bai && this.TicketbaiAvailable && this.GetConfigOption('optionBaiEnabled', false);
    }

    private _safedisable_sii = false;
    get SendSIIEnabled(): boolean {
        // VeriFactu wins if both flags were persisted (more common AEAT path).
        if (this.VerifactuAvailable && this.GetConfigOption('optionVftEnabled', false)){
            return false;
        }

        return !this._safedisable_sii && this.GetConfigOption('optionSiiEnabled', false);
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    /**
     * Place geolocation as `{lat, lng}` for `geocodeService.NearBy` / `DistanceTo`.
     * Adapts the related `Address` latitude/longitude; null when either is missing.
     */
    get Coordinates(): Coordinates | null {
        const _address = this.address as Address | null;
        if (!_address || (_address.latitude == null) || (_address.longitude == null)){
            return null;
        }
        return { lat: _address.latitude, lng: _address.longitude };
    }

    get IsValid() : boolean {
        return (!!this.status && (this.status == 'AC'));
    }    

    get IsExpired(): boolean {
        if (this.expires){
            return (this.expires.getTime() < new Date().getTime()); 
        }
        return true;
    }

    get IsExpiring(): boolean {
        const _days = this.DaysLeft;
        return _days !== null && _days <= 7;
    }

    get DaysLeft(): number | null {
        if (!this.expires || this.IsExpired){
            return null;
        }

        const _ms = this.expires.getTime() - Date.now();
        return Math.max(1, Math.ceil(_ms / 86400000));
    }

    private _onInitialize = new Subject <void> ();
    public OnInitialize = this._onInitialize.asObservable();
    
    Initialize(){
        this._onInitialize.next();
    }

}

